import axios from 'axios'

import {
  formatDateForServer,
  parseDateFromServer,
  formatTimeForServer,
  parseTimeFromServer,
} from '../../../functions/dateHandling'

import { getServerUrl } from '../../../functions/getServerUrl'

import {
  savingChartPhase,
  chartFieldSaved,
  errorSavingChartPhase,
  alertChartHasBeenSaved,
  errorSavingChartDate,
  handleChartFieldChange
 } from './chartFormReducer'


const defaultState = {
  id: '',
  patient_chart_id: '',
  date_of_onset: null,
  primary_infection_sites: [],
  showPrimaryInfectionSiteWarning: false,
  primaryInfectionSiteWarningMessage: '',
  showCustomPathogenWarning: false,
  customPathogenWarningMessage: '',
  showCustomSusceptibilityTestWarning: false,
  customSusceptibilityTestWarningMessage: '',


  // How was infection diagnosed?
  positive_bacterial_culture_results: false,
  fever: false,
  elevated_inflammatory_markers: false,
  other_infection_diagnosed: false,
  other_infection_diagnosed_description: '',


  confirmed_or_presumed_primary_source: '',
  confirmed_or_presumed_primary_source_description: '',

  primary_source_related_to_prosthetic: '',
  device_removed_or_replaced_date: null,
  primary_source_addressed: '',
  primary_source_addressed_date: null,
  primary_source_infection_details: '',
  displayTabs: false,
  displayPathogenQuestions: false,
  accordionActiveKey: 0,

  other_pathogen_description: "",

  cultureSampleTakenPathogenIdentified: false,

  infection_course_info_url: '',

  displayInfectionScreeningFailure: false,

  addingNewSusceptibilityTest: false,
}

export default function infectionCourseFormReducer(state = defaultState, action) {
  switch (action.type) {
    case "CHANGE_EDIT_CHART_PHASE":
      return { ...state, chartPhase: action.payload }
    case "HANDLE_INFECTION_COURSE_FORM_FIELD_CHANGE":
      return { ...state, [action.payload.key]: action.payload.value }
    case "SET_PRIMARY_INFECTION_SITE_WARNING_MESSAGE":
      return {
        ...state, 
        showPrimaryInfectionSiteWarning: !!action.payload,
        primaryInfectionSiteWarningMessage: action.payload || "",
      }
    case "SET_CUSTOM_PATHOGEN_WARNING_MESSAGE":
      return {
        ...state, 
        showCustomPathogenWarning: !!action.payload,
        customPathogenWarningMessage: action.payload || "",
      }
    case "SET_CUSTOM_SUSCEPTIBILITY_TEST_WARNING_MESSAGE":
      return {
        ...state, 
        showCustomSusceptibilityTestWarning: !!action.payload,
        customSusceptibilityTestWarningMessage: action.payload || "",
        addingNewSusceptibilityTest: false,
      }
    case "UPDATE_ACCORDION_ACTIVE_KEY":
      return { ...state, [action.payload.key]: action.payload.value }
    case "CHART_INFECTION_COURSE_INFO_HAS_BEEN_SAVED":
       return { ...state, infection_course_info_url: action.payload.infection_course_info_url }
    case "TOGGLE_INFECTION_SCREENING_FAILURE":
      return { ...state, displayInfectionScreeningFailure: action.payload }
    case "INFECTION_COURSE_ADDING_NEW_SUSCEPTIBILITY_TEST":
      return { ...state, addingNewSusceptibilityTest: true }
    case "INFECTION_COURSE_NEW_SUSCEPTIBILITY_TEST_ADDED":
      return { ...state, addingNewSusceptibilityTest: false }
    case "SET_CHART_FROM_SERVER_TO_FORM":
      if (action.payload.infection_course) {
        const data = resetInfectionCourseFromServer(action.payload)
        return {
          ...state,
          ...data
        }
      } else {
        return state
      }
    case "MARK_CHART_AS_COMPLETED":
      return defaultState
    case "CHART_HAS_BEEN_DELETED":
      return defaultState
    default:
      return state
  }
}





// ==================================================
// Infection Course
// ==================================================

const postInfectionCourse = (props) => {
  const infectionCourseInfo = formatInfectionCourseForServer(props)

  const {
    infection_course_info_url,
  } = props

  let method, url
  if (infection_course_info_url) {
    method = "PATCH"
    url = infection_course_info_url
  } else {
    method = "POST"
    url = `${getServerUrl()}/infection-courses/`
  }

  return axios({
    method,
    url,
    headers: { "Authorization": "JWT " + localStorage.getItem("jwt") },
    data: infectionCourseInfo,
  })
}


export const addInfectionCourse = (props, primarySiteName, primarySiteDescription) => {
  const {
    infection_course_info_url,
   } = props

  return (dispatch, getState) => {
    const chartIsSaving = getState().chartFormReducer.savingChartPhase
    if (chartIsSaving) {
      return dispatch({ type: '' })
    }
    dispatch(savingChartPhase())
    if (!infection_course_info_url) {
      postInfectionCourse(props)
      .then(response => {
        alertChartHasBeenSaved(props)
        const newInfectionCourseUrl = response.data.url
        dispatch(chartInfectionCourseHasBeenSaved(newInfectionCourseUrl))
        if (primarySiteName) {
          // post new primary site
          const newPrimarySite = generateNewPrimarySite(primarySiteName, props.primary_infection_sites, newInfectionCourseUrl, primarySiteDescription)
          return dispatch(submitChartPrimaryInfectionSiteInfo(props, newPrimarySite))
        }
      })
      .catch(error => handleInfectionCourseError(error, props, primarySiteName, dispatch))
    } else if (infection_course_info_url && primarySiteName) {
      // post new primary site
      const newPrimarySite = generateNewPrimarySite(primarySiteName, props.primary_infection_sites, infection_course_info_url, primarySiteDescription)
      return dispatch(submitChartPrimaryInfectionSiteInfo(props, newPrimarySite))
    } else {
      postInfectionCourse(props)
      .then(response => {
        alertChartHasBeenSaved(props)
        dispatch(chartInfectionCourseHasBeenSaved(response.data.url))
      })
      .catch(error => {
        handleInfectionCourseError(error, props, primarySiteName, dispatch)
      })
    }
  }
}


const handleInfectionCourseError = (error, props, name, dispatch) => {
  if (error.response.data.non_field_errors) {
    let joinError = error.response.data.non_field_errors.join(' ')
    let firstKey = joinError.split('[')[1].split(',')[0]
    if (joinError.split('[')[1].includes(',')) {
      //thirdKey will always point to override, if true date is overridable
      let secondKey = joinError.split('[')[1].split(',')[1].split(']')[0].split(' ')[1]
      let thirdKey = joinError.split('[')[1].split(',')[2].split(']')[0].split(' ')[1]
      
      if (props[`${firstKey}`] && props.chartDateErrorMessage.toLowerCase() === firstKey.toLowerCase()) {
        if (thirdKey === 'True') {
          //this will trigger the date override modal if the date can be overriden
          dispatch(handleChartFieldChange('triggerDateOverrideModal', true))
          dispatch(handleChartFieldChange('infectionCourseDateOverride', props))
        } else {
          props[`${firstKey}`] = null
          dispatch(addInfectionCourse(props, name))
          dispatch(handleInfectionCourseFieldChange(firstKey, null))
        }
      }
      if (props[`${secondKey}`] && props.chartDateErrorMessage.toLowerCase() === secondKey.toLowerCase()) {
        if (thirdKey === 'True') {
          //this will trigger the date override modal if the date can be overriden
          dispatch(handleChartFieldChange('triggerDateOverrideModal', true))
          dispatch(handleChartFieldChange('infectionCourseDateOverride', props))
        } else {
          props[`${secondKey}`] = null
          dispatch(addInfectionCourse(props, name))
          dispatch(handleInfectionCourseFieldChange(secondKey, null))
        }
      }
      return dispatch(errorSavingChartDate(error, "Infection Course"))
    } else {
      let key = firstKey.split(']')[0]
      if (props.chartDateErrorMessage.toLowerCase() === key.toLowerCase()) {
        props[`${key}`] = null
        dispatch(addInfectionCourse(props, name))
        dispatch(handleInfectionCourseFieldChange(key, null))
      }
    }
    return dispatch(errorSavingChartDate(error, "InfectionCourse"))
  } else {
    return dispatch(errorSavingChartPhase(error, 'the infection course form', dispatch))
  }
}

export const formatPathogenName = (name) => {
  if (typeof name !== 'string') {
    return name
  }

  const splitName = name.toLowerCase().split('')
  return splitName[0].toUpperCase() + splitName.slice(1).join('')
}


const resetInfectionCourseFromServer = (payload) => {
  const infectionCourse = payload.infection_course
  const dateOfOnset = parseDateFromServer(infectionCourse.date_of_onset)
  let formatedPrimarySites = []

  infectionCourse.primary_infection_sites.forEach(primarySite => {

    let formatedPathogens = []

    primarySite.pathogens.forEach(pathogen => {
      let formatedSusceptibilityTests = []

      pathogen.susceptibility_tests.forEach(test => {
        const susceptibilityTest = {
          id: pathogen.susceptibility_tests.indexOf(test) + 1,
          url: test.url,
          drug_name: test.drug_name,
          interpretation: test.interpretation,
          mic: test.mic,
          zone: test.zone,
          pathogen: test.pathogen,
          clsi: test.clsi,
          fda: test.fda,
          breakpoint_interpretation_unknown: test.breakpoint_interpretation_unknown,
          eu_cast: test.eu_cast,
          mic_and_zone_not_available: test.mic_and_zone_not_available,
          test_not_done: test.test_not_done,
        }
        formatedSusceptibilityTests.push(susceptibilityTest)
      })

      const gram_positive_non_anaerobe = pathogen.pathogens_in_same_culture.find(p => p.other_pathogen_in_same_culture === "gram_positive_non_anaerobe")
      const anaerobes = pathogen.pathogens_in_same_culture.find(p => p.other_pathogen_in_same_culture === "anaerobes")
      const fungi = pathogen.pathogens_in_same_culture.find(p => p.other_pathogen_in_same_culture === "fungi")

      pathogen = {
        id: primarySite.pathogens.indexOf(pathogen) + 1,
        url: pathogen.url,
        // name: pathogen.name ? pathogen.name.toLowerCase() : pathogen.name,
        name: pathogen.name ? formatPathogenName(pathogen.name) : pathogen.name,
        colony_count_greater_than_ten_to_the_fifth_power_per_ml: pathogen.colony_count_greater_than_ten_to_the_fifth_power_per_ml,
        patient_had_same_pathogen_prior_to_hospitalization: pathogen.patient_had_same_pathogen_prior_to_hospitalization,
        initial_positive_culture_date: parseDateFromServer(pathogen.initial_positive_culture_date),
        source_of_culture_sample_blood: pathogen.source_of_culture_sample_blood,
        source_of_culture_sample_respiratory: pathogen.source_of_culture_sample_respiratory,
        source_of_culture_sample_urine: pathogen.source_of_culture_sample_urine,
        source_of_culture_sample_gi_tract_fecal: pathogen.source_of_culture_sample_gi_tract_fecal,
        source_of_culture_sample_intra_abdominal: pathogen.source_of_culture_sample_intra_abdominal,
        source_of_culture_sample_bone_joint: pathogen.source_of_culture_sample_bone_joint,
        source_of_culture_sample_skin: pathogen.source_of_culture_sample_skin,
        source_of_culture_sample_other: pathogen.source_of_culture_sample_other,
        source_of_culture_sample_other_text: pathogen.source_of_culture_sample_other_text || '',
        date_index_culture_sample_taken: parseDateFromServer(pathogen.date_index_culture_sample_taken),
        // time_index_culture_sample_taken: pathogen.time_index_culture_sample_taken,
        time_index_culture_sample_taken: parseTimeFromServer(pathogen.time_index_culture_sample_taken),
        patient_location_when_index_culture_taken: pathogen.patient_location_when_index_culture_taken,
        patient_location_when_index_culture_taken_other_text: pathogen.patient_location_when_index_culture_taken_other_text,
        other_pathogen_description: pathogen.other_pathogen_description || '',
        susceptibility_tests: formatedSusceptibilityTests,
        primary_infection_site: pathogen.primary_infection_site,
        susceptibility_test_done: pathogen.susceptibility_test_done,
        date_of_test: parseDateFromServer(pathogen.date_of_test),
        rapid_test_method_used: pathogen.rapid_test_method_used,
        rapid_test_method_description: pathogen.rapid_test_method_description,

        no_other_pathogens_in_same_culture: pathogen.pathogens_in_same_culture.length === 0 && true,
        gram_positive_non_anaerobe: !!gram_positive_non_anaerobe && true,
        gram_positive_non_anaerobe_description: !!gram_positive_non_anaerobe ? gram_positive_non_anaerobe.other_pathogen_in_same_culture_description : '',
        anaerobes: !!anaerobes && true,
        anaerobes_description: !!anaerobes ? anaerobes.other_pathogen_in_same_culture_description : '',
        fungi: !!fungi && true,
        fungi_description: !!fungi ? fungi.other_pathogen_in_same_culture_description : ''
      }
      formatedPathogens.push(pathogen)
    })

    primarySite = {
        id: infectionCourse.primary_infection_sites.indexOf(primarySite) + 1,
        infection_course: infectionCourse.url,
        infection_site: primarySite.infection_site,
        infection_site_description: primarySite.infection_site_description,
        culture_sample_status: primarySite.culture_sample_status,
        pathogens: formatedPathogens,
        displayPathogenQuestions: primarySite.pathogens.length !== 0 ? true : false,
        url: primarySite.url,
      }

    formatedPrimarySites.push(primarySite)
  })
  return {
    date_of_onset: dateOfOnset,
    positive_bacterial_culture_results: infectionCourse.positive_bacterial_culture_results || false,
    fever: infectionCourse.fever || false,
    elevated_inflammatory_markers: infectionCourse.elevated_inflammatory_markers || false,
    other_infection_diagnosed: infectionCourse.other_infection_diagnosed || false,
    other_infection_diagnosed_description: infectionCourse.other_infection_diagnosed_description || '',
    confirmed_or_presumed_primary_source: infectionCourse.confirmed_or_presumed_primary_source || '',
    confirmed_or_presumed_primary_source_description: infectionCourse.confirmed_or_presumed_primary_source_description || '',
    primary_source_related_to_prosthetic: infectionCourse.primary_source_related_to_prosthetic || '',
    primary_source_addressed: infectionCourse.primary_source_addressed || '',
    device_removed_or_replaced_date: parseDateFromServer(infectionCourse.device_removed_or_replaced_date),
    primary_source_addressed_date: parseDateFromServer(infectionCourse.primary_source_addressed_date),
    primary_source_infection_details: infectionCourse.primary_source_infection_details || '',
    primary_infection_sites: formatedPrimarySites,
    infection_course_info_url: infectionCourse.url || '',
    displayTabs: infectionCourse.primary_infection_sites.length !== 0 ? true : false,
  }
}


export const handleInfectionCourseFieldChange = (key, value) => {
  return { type: "HANDLE_INFECTION_COURSE_FORM_FIELD_CHANGE", payload: { key, value } }
}


const chartInfectionCourseHasBeenSaved = (infection_course_info_url, goToNextPhase) => {
  return {
    type: "CHART_INFECTION_COURSE_INFO_HAS_BEEN_SAVED",
    payload: { infection_course_info_url, goToNextPhase },
  }
}


const formatInfectionCourseForServer = (props) => {
  const {
    patientChartUrl,
    date_of_onset,
  } = props

  let overrideDate = props.override_date
  if (overrideDate !== true) {
    overrideDate = false
  }

  let site

  if (props?.currentUser?.site?.url) {
    site = props.currentUser.site.url
  }

  const date = formatDateForServer(date_of_onset)

  return {
    date_of_onset: date,

    positive_bacterial_culture_results: props.positive_bacterial_culture_results || false,
    fever: props.fever || false,
    elevated_inflammatory_markers: props.elevated_inflammatory_markers || false,
    other_infection_diagnosed: props.other_infection_diagnosed || false,
    other_infection_diagnosed_description: props.other_infection_diagnosed_description || null,

    confirmed_or_presumed_primary_source: props.confirmed_or_presumed_primary_source || '',
    confirmed_or_presumed_primary_source_description: props.confirmed_or_presumed_primary_source_description || '',
    primary_source_related_to_prosthetic: props.primary_source_related_to_prosthetic || null,
    primary_source_addressed: props.primary_source_addressed || null,
    primary_source_infection_details: props.primary_source_infection_details || '',
    device_removed_or_replaced_date: formatDateForServer(props.device_removed_or_replaced_date),
    primary_source_addressed_date: formatDateForServer(props.primary_source_addressed_date),


    patient_chart: patientChartUrl,
    override_date: overrideDate,
    reason: props.dateOverrideReason || null,
    site,
  }
}
























// ==================================================
// Primary Infection Sites
// ==================================================

export const setCustomPrimaryInfectionSiteWarning = (warningMessage) => {
  return {
    type: "SET_PRIMARY_INFECTION_SITE_WARNING_MESSAGE",
    payload: warningMessage,
  }
}

export const updatePrimaryInfectionSite = (payload, primaryInfectionSites) => {
  const { primarySite, name, selection } = payload

  const primaryInfectionSiteIndex = primaryInfectionSites.findIndex(site => site.id === primarySite.id)

  const updatedInfectionSite = updateObject(primarySite, name, selection)

  const value =  resetNestedObjects(primaryInfectionSites, updatedInfectionSite, primaryInfectionSiteIndex)
  const key = 'primary_infection_sites'
  return {
    type: "HANDLE_INFECTION_COURSE_FORM_FIELD_CHANGE",
    payload: {key, value}
  }
}



const generateNewPrimarySite = (name, primaryInfectionSites, infection_course, primarySiteDescription) => {
  const newPrimarySiteId = primaryInfectionSites.find(test => test.id === primaryInfectionSites.length + 1) ? primaryInfectionSites.length + 2 : primaryInfectionSites.length + 1

  return {
    id: newPrimarySiteId,
    infection_course,
    infection_site: name,
    infection_site_description: primarySiteDescription || '',
    culture_sample_status: '',
    pathogens: [],
    displayPathogenQuestions: false,
  }
}


const formatPrimarySiteForServer = (site) => {
  return {
    infection_site: site.infection_site,
    infection_site_description: site.infection_site_description,
    culture_sample_status: site.culture_sample_status,
    infection_course: site.infection_course,
  }
}


const chartPrimaryInfectionSitesHaveBeenSaved = (infection_course_info_url) => {
  return {
    type: "CHART_INFECTION_COURSE_INFO_HAS_BEEN_SAVED",
    payload: { infection_course_info_url, goToNextPhase: false },
  }
}


export const submitChartPrimaryInfectionSiteInfo = (props, site) => {
  let infectionSiteToken
  if (typeof infectionSiteToken !== typeof undefined) {
    infectionSiteToken.cancel('Cancel primary site request')
  }
  // Set a new infectionSiteToken if one doesn't exist or the current one has been cancelled
  infectionSiteToken = axios.CancelToken.source()

  return (dispatch) => {
    dispatch(savingChartPhase())

    let method, url
    if (!site.url) {
      method = "POST"
      url = `${getServerUrl()}/primary-infection-sites/`
    } else {
      method = "PATCH"
      url = site.url
    }

    axios({
      method,
      url,
      headers: { "Authorization": "JWT " + localStorage.getItem("jwt") },
      params: {'infection_site': site.infection_site},
      data: formatPrimarySiteForServer(site),
      cancelToken: infectionSiteToken.token,
    })
    .then(response => {
      if (response?.data?.screening_failure) {
        dispatch({ type: "TOGGLE_INFECTION_SCREENING_FAILURE", payload: true })
        dispatch(handleChartFieldChange('screeningFailure', true))
      } else {
        dispatch({ type: "TOGGLE_INFECTION_SCREENING_FAILURE", payload: false })
        dispatch(handleChartFieldChange('screeningFailure', false))
      }
      if (site.url) {
        const payload = { primarySite: site, name: 'url', selection: response.data.url }
        dispatch(updatePrimaryInfectionSite(payload, props.primary_infection_sites))
      } else {
        const newInfectionSite = {...site, url: response.data.url}
        const updatedInfectionSites = [...props.primary_infection_sites, newInfectionSite]
        dispatch(handleInfectionCourseFieldChange('primary_infection_sites', updatedInfectionSites))
      }
      dispatch(setCustomPrimaryInfectionSiteWarning(''))
      dispatch(chartPrimaryInfectionSitesHaveBeenSaved(response.data.infection_course))
    })
    .catch(error => {
      if (error?.response?.data?.non_field_errors) {
        const nonFieldErrors = error.response.data.non_field_errors
          let nonFieldErrorsMessage = ''
          if (nonFieldErrors[0] && nonFieldErrors[0].includes('must make a unique set')) {
            nonFieldErrorsMessage = 'Warning: When adding multiple "Other" infection sites, please ensure that all of them have names and that their names are unique. If you wish to remove an infection site, click the checkbox next to it.'
          } else if (nonFieldErrors[0] && nonFieldErrors[0].toLowerCase().includes('select from the options')) {
            nonFieldErrorsMessage = nonFieldErrors[0]
          }

        if (nonFieldErrorsMessage.length > 0) {
          return dispatch(setCustomPrimaryInfectionSiteWarning(nonFieldErrorsMessage))
        } else {
          dispatch(errorSavingChartPhase('There was an error saving the infection course form'))
        }
      } else {
        dispatch(errorSavingChartPhase(error, 'the infection course form', dispatch))
      }
    })
  }
}



export const deletePrimarySiteFromServer = (infectionSiteUrl, props) => {
  return (dispatch) => {
    dispatch(savingChartPhase())
    let  method = "DELETE"
    let url = infectionSiteUrl

    axios({
      method,
      url,
      headers: { "Authorization": "JWT " + localStorage.getItem("jwt") },
    })
    .then(response => {
      let updatedInfectionSites = props.primary_infection_sites.filter(site => site.url !== infectionSiteUrl)
      dispatch(handleInfectionCourseFieldChange('primary_infection_sites', updatedInfectionSites))
      return dispatch(chartPrimaryInfectionSitesHaveBeenSaved(props.infection_course_info_url))
    })
    .catch(error => {
      dispatch(errorSavingChartPhase(error, 'the infection course form', dispatch))
    })
  }
}




// ==================================================
// Pathogens
// ==================================================


export const setCustomPathogenWarning = (warningMessage) => {
  return {
    type: "SET_CUSTOM_PATHOGEN_WARNING_MESSAGE",
    payload: warningMessage,
  }
}



export const updatePathogen = (primaryInfectionSites, primaryInfectionSite, pathogen, name, selection) => {
  const infectionSiteToUpdate = primaryInfectionSite

  const pathogenToUpdate = pathogen

  const pathogens = infectionSiteToUpdate.pathogens

  const primaryInfectionSiteIndex = primaryInfectionSites.findIndex(infectionSite => infectionSite.id === primaryInfectionSite.id)

  const pathogenIndex = pathogens.findIndex(path => path.id === pathogen.id)

  const updatedPathogen = updateObject(pathogenToUpdate, name, selection)

  if (primaryInfectionSiteIndex === -1) {
    return {type: ''}
  }

  let allPathogens

  if (pathogenIndex === -1) {
    allPathogens = [...pathogens, updatedPathogen]
  } else {
    allPathogens = resetNestedObjects(pathogens, updatedPathogen, pathogenIndex)
  }

  const updatedInfectionSite = updateObject(infectionSiteToUpdate, 'pathogens', allPathogens)

  const value = resetNestedObjects(primaryInfectionSites, updatedInfectionSite, primaryInfectionSiteIndex)

  const key = 'primary_infection_sites'

  return {
    type: "HANDLE_INFECTION_COURSE_FORM_FIELD_CHANGE",
    payload: {key, value}
  }
}



export const deletePathogenFromServer = (pathogenToRemove, props) => {
  return (dispatch) => {
    dispatch(savingChartPhase())
    let  method = "DELETE"
    let url = pathogenToRemove.url
    axios({
      method,
      url,
      headers: { "Authorization": "JWT " + localStorage.getItem("jwt") },
    })
    .then(response => {
      return dispatch(chartPrimaryInfectionSitesHaveBeenSaved(props.infection_course_info_url))
    })
    .catch(error => {
      dispatch(errorSavingChartPhase(error, 'the infection course form', dispatch))
    })
    .finally(() => {
      dispatch(chartFieldSaved())
    })
  }
}


export const generateNewPathogen = (name, primarySite, pathogenDescription) => {
  return {
    id: primarySite.pathogens.find(pathogen => pathogen.id === primarySite.pathogens.length + 1) ? primarySite.pathogens.length + 2 : primarySite.pathogens.length + 1,
    primary_infection_site: primarySite.url,
    name: name ? name.toLowerCase() : name,
    other_pathogen_description: pathogenDescription,
    colony_count_greater_than_ten_to_the_fifth_power_per_ml: false,
    patient_had_same_pathogen_prior_to_hospitalization: '',
    initial_positive_culture_date: null,
    source_of_culture_sample_blood: false,
    source_of_culture_sample_respiratory: false,
    source_of_culture_sample_urine: false,
    source_of_culture_sample_gi_tract_fecal: false,
    source_of_culture_sample_intra_abdominal: false,
    source_of_culture_sample_bone_joint: false,
    source_of_culture_sample_skin: false,
    source_of_culture_sample_other: false,
    source_of_culture_sample_other_text: '',
    date_index_culture_sample_taken: null,
    time_index_culture_sample_taken: null,
    patient_location_when_index_culture_taken: '',
    patient_location_when_index_culture_taken_other_text: '',
    susceptibility_tests: [],
    susceptibility_test_done: false,
    date_of_test: null,
    rapid_test_method_used: null,
    rapid_test_method_description: '',
    no_other_pathogens_in_same_culture: true,
    gram_positive_non_anaerobe: false,
    gram_positive_non_anaerobe_description: '',
    anaerobes: false,
    anaerobes_description: '',
    fungi: false,
    fungi_description: ''
  }
}


export const formatPathogenForServer = (pathogen) => {
  return {
    // name: pathogen.name ? pathogen.name.toLowerCase() : pathogen.name,
    name: pathogen.name ? formatPathogenName(pathogen.name) : pathogen.name,
    colony_count_greater_than_ten_to_the_fifth_power_per_ml: pathogen.colony_count_greater_than_ten_to_the_fifth_power_per_ml,
    patient_had_same_pathogen_prior_to_hospitalization: pathogen.patient_had_same_pathogen_prior_to_hospitalization,
    initial_positive_culture_date: formatDateForServer(pathogen.initial_positive_culture_date),
    source_of_culture_sample_blood: pathogen.source_of_culture_sample_blood,
    source_of_culture_sample_respiratory: pathogen.source_of_culture_sample_respiratory,
    source_of_culture_sample_urine: pathogen.source_of_culture_sample_urine,
    source_of_culture_sample_gi_tract_fecal: pathogen.source_of_culture_sample_gi_tract_fecal,
    source_of_culture_sample_intra_abdominal: pathogen.source_of_culture_sample_intra_abdominal,
    source_of_culture_sample_bone_joint: pathogen.source_of_culture_sample_bone_joint,
    source_of_culture_sample_skin: pathogen.source_of_culture_sample_skin,
    source_of_culture_sample_other: pathogen.source_of_culture_sample_other,
    source_of_culture_sample_other_text: pathogen.source_of_culture_sample_other_text,
    date_index_culture_sample_taken: formatDateForServer(pathogen.date_index_culture_sample_taken),
    // time_index_culture_sample_taken: formatDateForServer(pathogen.time_index_culture_sample_taken),
    time_index_culture_sample_taken: formatTimeForServer(pathogen.time_index_culture_sample_taken),
    patient_location_when_index_culture_taken: pathogen.patient_location_when_index_culture_taken,
    patient_location_when_index_culture_taken_other_text: pathogen.patient_location_when_index_culture_taken_other_text,
    other_pathogen_description: pathogen.other_pathogen_description || '',
    primary_infection_site: pathogen.primary_infection_site,
    susceptibility_test_done: pathogen.susceptibility_test_done,
    date_of_test: formatDateForServer(pathogen.date_of_test),
    rapid_test_method_used: !!pathogen.rapid_test_method_used,
    rapid_test_method_description: pathogen.rapid_test_method_description,
    no_other_pathogens_in_same_culture: pathogen.no_other_pathogens_in_same_culture,
    gram_positive_non_anaerobe: pathogen.gram_positive_non_anaerobe,
    gram_positive_non_anaerobe_description: pathogen.gram_positive_non_anaerobe_description,
    anaerobes: pathogen.anaerobes,
    anaerobes_description: pathogen.anaerobes_description,
    fungi: pathogen.fungi,
    fungi_description: pathogen.fungi_description
  }
}



export const submitChartPathogenInfo = (props, pathogenInfo) => {
  const { primary_infection_sites } = props

  const { pathogen, primaryInfectionSite } = pathogenInfo

  return (dispatch, getState) => {
    const chartIsSaving = getState().chartFormReducer.savingChartPhase
    if (chartIsSaving) {
      return dispatch({ type: '' })
    }

    dispatch(savingChartPhase())
    const data = formatPathogenForServer(pathogen)
    if (props.override_date) {
      data.override_date = true
      data.reason = props.dateOverrideReason
    }

    let method, url
    if (!pathogen.url) {
      method = "POST"
      url = `${getServerUrl()}/pathogens/`
    } else {
      method = "PATCH"
      url = pathogen.url
    }

    axios({
      method,
      url,
      headers: { "Authorization": "JWT " + localStorage.getItem("jwt") },
      data,
    })
    .then(response => {
      if (!pathogen.url) {
        dispatch(updatePathogen(primary_infection_sites, primaryInfectionSite, pathogen, 'url', response.data.url))
      }

      if (!response.data.pathogens_in_same_culture.length) {
        //if there are no pathogens in same culture, reset all relevant values
        let updatedPathogen = {...pathogen}
        updatedPathogen['url'] = response.data.url
        updatedPathogen['gram_positive_non_anaerobe'] = false
        updatedPathogen['anaerobes'] = false
        updatedPathogen['fungi'] = false
        updatedPathogen['gram_positive_non_anaerobe_description'] = ''
        updatedPathogen['anaerobes_description'] = ''
        updatedPathogen['fungi_description'] = ''

        //when selecting pathogens, if its a new pathogen it isn't yet in primaryInfectionSite.pathogens so add it if it doesnt exist
        let currentPathogens
        if (!primaryInfectionSite.pathogens.find(p => p.id === pathogen.id)) {
          currentPathogens = [...primaryInfectionSite.pathogens, updatedPathogen]
        } else {
          currentPathogens = [...primaryInfectionSite.pathogens]
        }

        let updatedPathogens = currentPathogens.map(pathogen => {
          if (pathogen.id === updatedPathogen.id) {
            pathogen = updatedPathogen
            return pathogen
          }
          return pathogen
        })

        const payload = { primarySite: primaryInfectionSite, name: 'pathogens', selection: updatedPathogens }
        dispatch(updatePrimaryInfectionSite(payload, props.primary_infection_sites))
      }
      dispatch(setCustomPathogenWarning(''))
      dispatch(chartPrimaryInfectionSitesHaveBeenSaved(props.infection_course_info_url))
    })
    .catch(error => {
      if (error?.response?.data?.non_field_errors) {
        const nonFieldErrors = error.response.data.non_field_errors
        let nonFieldErrorsMessage = ''
        if (nonFieldErrors[0] && nonFieldErrors[0].includes('must make a unique set')) {
          nonFieldErrorsMessage = 'Warning: When adding multiple "Other" pathogens, please ensure that all of them have names and that their names are unique. If you wish to remove a pathogen, click the checkbox next to it.'
        } else if (nonFieldErrors[0] && nonFieldErrors[0].toLowerCase().includes('select a pathogen')) {
          nonFieldErrorsMessage = nonFieldErrors[0]
        }

        if (nonFieldErrorsMessage.length > 0) {
          if (!pathogen.other_pathogen_description || !pathogen.url) {
            const filteredPathogens = primaryInfectionSite.pathogens.filter(p => p.id !== pathogen.id)
            const payload = {
              primarySite: primaryInfectionSite,
              name: 'pathogens',
              selection: filteredPathogens,
            }
            dispatch(updatePrimaryInfectionSite(payload, props.primary_infection_sites))
          }
          return dispatch(setCustomPathogenWarning(nonFieldErrorsMessage))
        } else {
          let joinError = nonFieldErrors.join(' ')
          let firstKey = joinError.split('[')[1].split(',')[0]
          if (joinError.split('[')[1].includes(',')) {
            //thirdKey will always point to override, if true date is overridable
            let secondKey = joinError.split('[')[1].split(',')[1].split(']')[0].split(' ')[1]
            let thirdKey = joinError.split('[')[1].split(',')[2].split(']')[0].split(' ')[1]
            if (pathogenInfo.pathogen[`${firstKey}`] && pathogenInfo.chartDateErrorMessage.toLowerCase() === firstKey.toLowerCase()) {
              if (thirdKey === 'True') {
                //this will trigger the date override modal if the date can be overriden
                let dateData = {props: {...props}, pathogenInfo: pathogenInfo}
                dispatch(handleChartFieldChange('triggerDateOverrideModal', true))
                dispatch(handleChartFieldChange('pathogenDateOverride', dateData))
              } else {
                pathogenInfo.pathogen[`${firstKey}`] = null
                dispatch(submitChartPathogenInfo(props, pathogenInfo))
                dispatch(updatePathogen(primary_infection_sites, primaryInfectionSite, pathogen, firstKey, null))
              }
            }
            if (pathogenInfo.pathogen[`${secondKey}`] && pathogenInfo.chartDateErrorMessage.toLowerCase() === secondKey.toLowerCase()) {
              if (thirdKey === 'True') {
                //this will trigger the date override modal if the date can be overriden
                let dateData = {props: {...props}, pathogenInfo: pathogenInfo}
                dispatch(handleChartFieldChange('triggerDateOverrideModal', true))
                dispatch(handleChartFieldChange('pathogenDateOverride', dateData))
              } else {
                pathogenInfo.pathogen[`${secondKey}`] = null
                dispatch(submitChartPathogenInfo(props, pathogenInfo))
                dispatch(updatePathogen(primary_infection_sites, primaryInfectionSite, pathogen, secondKey, null))
              }
            }
          } else {
            let key = firstKey.split(']')[0]
            if (pathogenInfo.chartDateErrorMessage.toLowerCase() === key.toLowerCase()) {
              pathogenInfo.pathogen[`${key}`] = null
              dispatch(submitChartPathogenInfo(props, pathogenInfo))
              dispatch(updatePathogen(primary_infection_sites, primaryInfectionSite, pathogen, key, null))
            }
          }
        }
        return dispatch(errorSavingChartDate(error, "Pathogen"))
      } else {
        dispatch(errorSavingChartPhase(error, 'Pathogen', dispatch))
      }
    })
    .finally(() => {
      try {
        if (pathogen.susceptibility_test_done === true) {
          if (!pathogen.susceptibility_tests.find(test => test.drug_name === 'Cefiderocol')) {
            props.addNewSusceptibilityTest(primaryInfectionSite, pathogen, 'Cefiderocol')
          }
        }
      } catch (error) {
        return
      }
    })
  }
}




// ==================================================
// Susceptibility Tests
// ==================================================


export const addingNewSusceptibilityTest = () => {
  return {type: 'INFECTION_COURSE_ADDING_NEW_SUSCEPTIBILITY_TEST'}
}

export const newSusceptibilityTestAdded = () => {
  return {type: 'INFECTION_COURSE_NEW_SUSCEPTIBILITY_TEST_ADDED'}
}

export const setCustomSusceptibilityTestWarning = (warningMessage) => {
  return {
    type: "SET_CUSTOM_SUSCEPTIBILITY_TEST_WARNING_MESSAGE",
    payload: warningMessage,
  }
}

const formatSusceptibilityTestsForServer = (susceptibilityTest) => {
  return { 
    pathogen: susceptibilityTest.pathogen,
    drug_name: susceptibilityTest.drug_name || null,
    interpretation: susceptibilityTest.interpretation || null,
    mic: susceptibilityTest.mic || null,
    zone: susceptibilityTest.zone || null,
    mic_and_zone_not_available: susceptibilityTest.mic_and_zone_not_available || false,
    clsi: susceptibilityTest.clsi || false,
    fda: susceptibilityTest.fda || false,
    breakpoint_interpretation_unknown: susceptibilityTest.breakpoint_interpretation_unknown || false,
    eu_cast: susceptibilityTest.eu_cast || false,
    test_not_done: susceptibilityTest.test_not_done || false
}
}


export const submitChartSusceptibilityTestInfo = (props, susceptibilityTest, pathogen) => {
  return (dispatch, getState) => {
    const chartIsSaving = getState().chartFormReducer.savingChartPhase
    if (chartIsSaving) {
      return dispatch({ type: '' })
    }

    dispatch(savingChartPhase())

    let method, url
    if (!susceptibilityTest.url) {
      method = "POST"
      url = `${getServerUrl()}/susceptibility-tests/`
    } else {
      method = "PATCH"
      url = susceptibilityTest.url
    }

    axios({
      method,
      url,
      headers: { "Authorization": "JWT " + localStorage.getItem("jwt") },
      // data: susceptibilityTest,
      data: formatSusceptibilityTestsForServer(susceptibilityTest)
    })
    .then(response => {
      // if (!susceptibilityTest.url) {
        const { primary_infection_sites } = props
        const infectionSiteIndex = primary_infection_sites.findIndex(site => pathogen.primary_infection_site === site.url)
        const infectionSite = primary_infection_sites[infectionSiteIndex]

        const pathogenIndex = infectionSite.pathogens.findIndex(path => path.url === pathogen.url)

        const susceptibilityTestIndex = pathogen.susceptibility_tests.findIndex(susTest => susTest.id === susceptibilityTest.id)

        const updatedSusceptibilityTest = {...susceptibilityTest, url: response.data.url}
        let updatedSusceptibilityTestList

        if (susceptibilityTestIndex > -1) {
          updatedSusceptibilityTestList = resetNestedObjects([...pathogen.susceptibility_tests], updatedSusceptibilityTest, susceptibilityTestIndex)
        } else {
          updatedSusceptibilityTestList = [...pathogen.susceptibility_tests, updatedSusceptibilityTest]
        }

        const updatedPathogen = {...pathogen, susceptibility_tests: updatedSusceptibilityTestList}
        const updatedPathogenList = resetNestedObjects([...infectionSite.pathogens], updatedPathogen, pathogenIndex)

        const updatedInfectionSite = {...infectionSite, pathogens: updatedPathogenList}
        const updatedInfectionSiteList = resetNestedObjects([...primary_infection_sites], updatedInfectionSite, infectionSiteIndex)

        dispatch ({
          type: "HANDLE_INFECTION_COURSE_FORM_FIELD_CHANGE",
          payload: { key: 'primary_infection_sites', value: updatedInfectionSiteList },
        })
      // }

      dispatch(newSusceptibilityTestAdded())
      dispatch(setCustomSusceptibilityTestWarning(''))

      return dispatch(chartPrimaryInfectionSitesHaveBeenSaved(props.infection_course_info_url))
    })
    .catch(error => {
      if (error?.response?.data?.non_field_errors) {
        const nonFieldErrors = error.response.data.non_field_errors
        if (nonFieldErrors[0] && nonFieldErrors[0].toLowerCase().includes('validation error')) {
          return dispatch(setCustomSusceptibilityTestWarning(nonFieldErrors[0]))
        }
        return dispatch(errorSavingChartDate(error, "Susceptibility Test"))
      } else {
        dispatch(errorSavingChartPhase(error, 'the infection course form', dispatch))
      }
    })
  }
}



export const deleteSusceptibilityTestFromServer = (susceptibilityTestToRemove, props) => {
  return (dispatch) => {
    dispatch(savingChartPhase())
    let  method = "DELETE"
    let url = susceptibilityTestToRemove.url
    axios({
      method,
      url,
      headers: { "Authorization": "JWT " + localStorage.getItem("jwt") },
    })
    .then(response => {
      return dispatch(chartPrimaryInfectionSitesHaveBeenSaved(props.infection_course_info_url))
    })
    .catch(error => {
      dispatch(errorSavingChartPhase(error, 'the infection course form', dispatch))
    })
    .finally(() => {
      dispatch(chartFieldSaved())
    })
  }
}


export const addSusceptibilityTest = (props, primaryInfectionSites, primaryInfectionSite, pathogen, name, dateOfTest) => {

  // const primaryInfectionSiteIndex = primaryInfectionSites.indexOf(primaryInfectionSite)
  const primaryInfectionSiteIndex = primaryInfectionSites.findIndex(site => site.id === primaryInfectionSite.id)
  const infectionSiteToUpdate = primaryInfectionSites[primaryInfectionSiteIndex]

  const pathogenToUpdate = pathogen

  const newSusceptibilityTest = {
    id: pathogenToUpdate.susceptibility_tests.find(test => test.id === pathogenToUpdate.susceptibility_tests.length + 1) ? pathogenToUpdate.susceptibility_tests.length + 2 : pathogenToUpdate.susceptibility_tests.length + 1,
    pathogen: pathogenToUpdate.url,
    drug_name: name,
    susceptibile: false,
    intermediate: false,
    resistant: false,
    not_interpretable: false,
    mic: '',
    zone: '',
    clsi: false,
    fda: false,
    breakpoint_interpretation_unknown: false,
    eu_cast: false,
    mic_and_zone_not_available: false,
    test_not_done: false,
  }

  let susceptibilityTests = [...pathogenToUpdate.susceptibility_tests, newSusceptibilityTest]

  // const updatedPathogen = updateObject(pathogenToUpdate, 'susceptibility_tests', susceptibilityTests)

  const pathogenIndex = primaryInfectionSite.pathogens.findIndex(path => path.id === pathogen.id)


  primaryInfectionSite.pathogens[pathogenIndex].susceptibility_tests = susceptibilityTests

  const updatedInfectionSite = updateObject(infectionSiteToUpdate, 'pathogens', primaryInfectionSite.pathogens)

  const value = resetNestedObjects(primaryInfectionSites, updatedInfectionSite, primaryInfectionSiteIndex)

  const key = 'primary_infection_sites'

  return {
    type: "HANDLE_INFECTION_COURSE_FORM_FIELD_CHANGE",
    payload: {key, value}
  }
}

export const updateSusceptibilityTests = (primaryInfectionSites, primaryInfectionSite, pathogen, susceptibilityTest, name, selection) => {
  const susceptibilityTestIndex = pathogen.susceptibility_tests.indexOf(susceptibilityTest)

  const pathogenIndex = primaryInfectionSite.pathogens.indexOf(pathogen)

  const primaryInfectionSiteIndex = primaryInfectionSites.indexOf(primaryInfectionSite)

  let updatedSusceptibilityTest

  if (name === 'clsi' || name === 'fda' || name === 'eu_cast' || name === 'breakpoint_interpretation_unknown') {
    updatedSusceptibilityTest = {
      ...susceptibilityTest,
      clsi: false,
      fda: false,
      eu_cast: false,
      breakpoint_interpretation_unknown: false,
      [name]: selection,
    }
  } else {
    updatedSusceptibilityTest = updateObject(susceptibilityTest, name, selection)
  }

  pathogen.susceptibility_tests[susceptibilityTestIndex] = updatedSusceptibilityTest

  const updatedPathogen = updateObject(pathogen, 'susceptibility_tests', pathogen.susceptibility_tests)

  const allPathogens = resetNestedObjects(primaryInfectionSite.pathogens, updatedPathogen, pathogenIndex)

  const updatedInfectionSite = updateObject(primaryInfectionSite, 'pathogens', allPathogens)

  const value = resetNestedObjects(primaryInfectionSites, updatedInfectionSite, primaryInfectionSiteIndex)
  const key = 'primary_infection_sites'
  return {
    type: "HANDLE_INFECTION_COURSE_FORM_FIELD_CHANGE",
    payload: {key, value}
  }
}




// ==================================================
// MISC
// ==================================================



export const updateAccordionActiveKey = (key, value) => {
  return {
    type: "UPDATE_ACCORDION_ACTIVE_KEY",
    payload: {key, value},
  }
}



export const updateObject = (object, key, value) => {
  return { ...object, [key]: value}
}


const resetNestedObjects = (objects, updatedObject, index) => {
  return [
    ...objects.slice(0, index),
    updatedObject,
    ...objects.slice(index + 1),
  ]
}
